<script setup lang="ts">
  import {
    type CustomerCase as Case,
    type CustomerCase,
    type GenericDataMapper
  } from '@/types/generated/types.gen'
  import { computed, onMounted, type PropType } from 'vue'
  import SkeletonLoader from '@/components/skeleton/SkeletonLoader.vue'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import router from '@/router'
  import { useUserStore } from '@/shared/stores/userStore'

  const { fetchUser } = useUserStore()
  const caseStore = useCaseStore()
  const caseRecord = computed<Case | null>(() => caseStore.caseRecord)
  const nonEmptyLawSpecificMappers = computed<GenericDataMapper[]>(
    () => caseStore.nonEmptyLawSpecificMappers
  )

  const isOnLawSpecificDataPage = computed<boolean>(() => {
    return window.location.pathname.includes('law-specific-data')
  })

  const isOnEntitiesPage = computed<boolean>(() => {
    return window.location.pathname.includes('entities')
  })

  const isOnAiChatPage = computed<boolean>(() => {
    return window.location.pathname.includes('chat')
  })

  onMounted(async () => {
    await Promise.all([
      caseStore.loadCase(router.currentRoute.value.params.uuid as string),
      fetchUser()
    ])
  })
</script>

<template>
  <div
    v-if="caseRecord"
    class="flex min-h-screen flex-col"
  >
    <div class="legacy-bg-color flex flex-grow items-start justify-center">
      <div class="container px-16 py-8">
        <div class="legacy-case-title mb-2 flex items-center justify-between">
          Anfrage von
          {{
            `${caseRecord?.data.client.first_name.charAt(0)}. ${caseRecord?.data.client.last_name}`
          }}
        </div>
        <nav class="flex gap-x-2">
          <a
            class="active inline-flex cursor-pointer items-center gap-x-2 whitespace-nowrap px-1 py-2 text-sm
              hover:text-purple-600 focus:text-purple-600 focus:outline-none disabled:pointer-events-none
              disabled:opacity-50"
            :href="`/cases/${caseRecord.id}/`"
          >
            Allgemein
          </a>
          <router-link
            class="inline-flex cursor-pointer items-center gap-x-2 whitespace-nowrap px-1 py-2 text-sm text-gray-500
              hover:text-purple-600 focus:text-purple-600 focus:outline-none disabled:pointer-events-none
              disabled:opacity-50"
            :class="
              isOnLawSpecificDataPage
                ? 'border-b-2 border-purple-600 font-semibold text-purple-600'
                : ''
            "
            :to="`/app/cases/${caseRecord.uuid}/law-specific-data`"
          >
            Rechtsbezogene Daten
            <span
              v-if="caseRecord.data.mappers.length > 0"
              class="badge bg-purple"
            >
              {{ nonEmptyLawSpecificMappers.length }}
            </span>
            <SkeletonLoader
              v-else
              class="h-5 w-5"
            />
          </router-link>
          <router-link
            class="inline-flex cursor-pointer items-center gap-x-2 whitespace-nowrap px-1 py-2 text-sm text-gray-500
              hover:text-purple-600 focus:text-purple-600 focus:outline-none disabled:pointer-events-none
              disabled:opacity-50"
            :class="
              isOnEntitiesPage ? 'border-b-2 border-purple-600 font-semibold text-purple-600' : ''
            "
            :to="`/app/cases/${caseRecord.uuid}/entities`"
          >
            Beteiligte
            <span
              v-if="caseRecord.data.entities.length > 0"
              class="badge bg-purple"
            >
              {{ caseRecord.data.entities.length }}
            </span>
            <SkeletonLoader
              v-else
              class="h-5 w-5"
            />
          </router-link>
          <router-link
            :to="`/app/cases/${caseRecord.uuid}/chat`"
            class="inline-flex cursor-pointer items-center gap-x-2 whitespace-nowrap px-1 py-2 text-sm text-gray-500
              hover:text-purple-600 focus:text-purple-600 focus:outline-none disabled:pointer-events-none
              disabled:opacity-50"
            :class="
              isOnAiChatPage ? 'border-b-2 border-purple-600 font-semibold text-purple-600' : ''
            "
          >
            KI Chat
          </router-link>
        </nav>
        <slot></slot>
      </div>
    </div>
  </div>
</template>

<style scoped>
  a:hover {
    text-decoration: none;
  }

  .legacy-bg-color {
    background-color: #f8fafc !important;
  }

  .legacy-case-title {
    font-weight: 600 !important;
    white-space: nowrap !important;
    font-size: 1.5rem !important;
    line-height: 1.3333333;
    margin-top: 0;
    margin-bottom: 0.5rem;
    margin-block-end: 0.67em;
    margin-inline-start: 0px;
    margin-inline-end: 0px;
    unicode-bidi: isolate;
    text-rendering: optimizeLegibility;
    font-feature-settings: 'liga' 0;
    letter-spacing: 0;
  }
</style>
